import { useUpsellsList, type UpsellItem } from '@/lib/api/upsells';
import React, { createContext, useContext } from 'react';
import { useParams } from 'react-router-dom';

interface UpsellsContextValue {
  upsells: UpsellItem[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

const UpsellsContext = createContext<UpsellsContextValue | undefined>(undefined);

interface UpsellsProviderProps {
  children: React.ReactNode;
  storeId?: string;
}

export const UpsellsProvider: React.FC<UpsellsProviderProps> = ({ children, storeId: storeIdProp }) => {
  const { id } = useParams<{ id: string }>();
  const storeId = storeIdProp || id;

  if (!storeId) {
    throw new Error('Store id is not defined');
  }

  // Refetch upsells when provider mounts to ensure fresh data
  const {
    data,
    isLoading,
    error: queryError,
    refetch: refetchQuery,
  } = useUpsellsList(
    {
      storeId,
    },
    {
      refetchOnMount: 'always', // Always refetch when mounted
      refetchOnWindowFocus: false, // Don't refetch on window focus
    },
  );

  const upsells = data?.upsells ?? [];
  const loading = isLoading;
  const error = queryError ? new Error(queryError.message) : null;

  const refetch = async () => {
    await refetchQuery();
  };

  const value: UpsellsContextValue = {
    upsells,
    loading,
    error,
    refetch,
  };

  return <UpsellsContext.Provider value={value}>{children}</UpsellsContext.Provider>;
};

export const useUpsellsContext = () => {
  const context = useContext(UpsellsContext);

  if (!context) {
    throw new Error('useUpsellsContext must be used within an UpsellsProvider');
  }

  return context;
};

/**
 * Safe version that returns null instead of throwing
 * Use this when the provider might not be available
 */
export const useUpsellsContextSafe = () => {
  const context = useContext(UpsellsContext);
  return context || null;
};
